/**
 * Cálculo de intereses por mora para propiedad horizontal en Uruguay.
 *
 * Marco legal:
 * - Ley 19.604: Interés máximo 12% anual simple.
 * - No se capitalizan intereses (interés sobre interés prohibido).
 * - Reajuste según Decreto Ley 14.500 (Unidad Reajustable).
 * - Prescripción: 4 años.
 */

import { roundPesos } from "./redondeoPesos";

/**
 * Calcula los días entre dos fechas.
 * @param {Date|string|{toDate:Function}|{seconds:number}} from
 * @param {Date|string|{toDate:Function}|{seconds:number}} to
 * @returns {number} días (enteros, >= 0)
 */
export function diffDays(from, to) {
  const a = toJSDate(from);
  const b = toJSDate(to);
  if (!a || !b) return 0;
  const ms = b.getTime() - a.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

/**
 * Convierte distintos formatos de fecha a Date nativo.
 * Soporta: Date, Firestore Timestamp, string ISO, string DD/MM/YYYY, {seconds}.
 * @param {*} val
 * @returns {Date|null}
 */
export function toJSDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") {
    const ddmmyyyy = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) {
      return new Date(Number(ddmmyyyy[3]), Number(ddmmyyyy[2]) - 1, Number(ddmmyyyy[1]));
    }
    // `YYYY-MM-DD` se parsea como UTC-medianoche → en UTC-3 cae al día anterior
    // (off-by-one en comparaciones de vencimiento/mora). Se ancla a mediodía local,
    // igual que fundMovements.parseDate.
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(val) ? `${val}T12:00:00` : val;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "number") return new Date(val);
  return null;
}

const rnd = (x) => Math.round((Number(x) + Number.EPSILON) * 100) / 100;

/**
 * Fecha de pago EFECTIVA de un payment (cuándo se pagó realmente), con la misma
 * prioridad que usa fundMovements.js: paymentDate (residente) > adminPaymentDate
 * (pago manual) > verifiedAt (verificación) > createdAt. Usada para cobrar mora
 * por los días entre el vencimiento y el pago real.
 * @param {Object} payment
 * @returns {Date|null}
 */
export function getEffectivePaidDate(payment) {
  if (!payment) return null;
  return (
    toJSDate(payment.paymentDate) ||
    toJSDate(payment.adminPaymentDate) ||
    toJSDate(payment.verifiedAt) ||
    toJSDate(payment.createdAt) ||
    null
  );
}

/**
 * Fin del período de gracia (última fecha SIN mora/multa), como ISO "YYYY-MM-DD".
 * Según building.gracePeriodMode:
 *  - "dias" (default): maturity + gracePeriodDays (días corridos, comportamiento histórico).
 *  - "fin_de_mes": el último día del mes del vencimiento (ej. vence 15/ago, gracia hasta 31/ago).
 *  - "dia_fijo": hasta building.graceUntilDay de ese mes (capado al último día del mes).
 * @param {string} maturityIso  Vencimiento "YYYY-MM-DD".
 * @param {Object} building
 * @returns {string} ISO "YYYY-MM-DD".
 */
export function computeGraceEnd(maturityIso, building) {
  const s = String(maturityIso || "");
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return maturityIso;
  const iso = (yy, mm, dd) => `${yy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  const lastDay = new Date(y, m, 0).getDate(); // último día del mes del vencimiento
  const mode = building?.gracePeriodMode || "dias";
  if (mode === "fin_de_mes") return iso(y, m, lastDay);
  if (mode === "dia_fijo") {
    const gd = Math.min(Number(building?.graceUntilDay) || lastDay, lastDay);
    return iso(y, m, Math.max(d, gd)); // nunca antes del vencimiento
  }
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + (Number(building?.gracePeriodDays) || 0));
  return iso(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

/**
 * Calcula la mora por atraso (Ley 19.604): reajuste por indexación (Decreto-Ley
 * 14.500, vía UI) + interés simple del 12% anual NO capitalizable sobre el
 * capital reajustado.
 *
 * Soporta dos modos de período:
 *  - LEGACY: pasar `dueDate` + `calculationDate` (+ `gracePeriodDays`). El
 *    devengo va de (dueDate + gracia) a calculationDate. factorUI = 1.
 *  - INCREMENTAL (nuevo): pasar `accrualStartDate` + `accrualEndDate` explícitos
 *    (el llamador ya descontó gracia y lo ya cobrado) + `uiStart`/`uiEnd` para
 *    indexar. Tiene prioridad sobre dueDate/calculationDate.
 *
 * Indexación: si `uiStart` y `uiEnd` son > 0, factorUI = uiEnd/uiStart y el
 * capital se reajusta; si no, factorUI = 1 (solo interés, comportamiento previo).
 *
 * @returns {{ interest:number, reajuste:number, moraTotal:number,
 *   capitalReajustado:number, daysOverdue:number, effectiveDays:number,
 *   rate:number, dailyRate:number, method:string, factorUI:number,
 *   uiStart:number|null, uiEnd:number|null }}
 */
export function calculateMoraInterest({
  originalDebt,
  dueDate,
  calculationDate,
  annualRate,
  gracePeriodDays = 0,
  interestMethod = "porcentaje",
  interestFixedAmount = 0,
  accrualStartDate = null,
  accrualEndDate = null,
  uiStart = null,
  uiEnd = null,
}) {
  const debt = Number(originalDebt) || 0;
  const rate = Math.min(Number(annualRate) || 0, 12); // tope legal 12%
  const grace = Math.max(0, Number(gracePeriodDays) || 0);

  // Determinar el período de devengo (start → end).
  let start;
  let end;
  if (accrualStartDate && accrualEndDate) {
    start = toJSDate(accrualStartDate);
    end = toJSDate(accrualEndDate);
  } else {
    const due = toJSDate(dueDate);
    end = toJSDate(calculationDate) || new Date();
    start = due ? new Date(due.getTime()) : null;
    if (start) start.setDate(start.getDate() + grace); // descontar gracia
  }
  const days = start && end ? Math.max(0, Math.floor((end.getTime() - start.getTime()) / 86400000)) : 0;
  // Días de atraso totales (informativo): desde el vencimiento hasta el fin del devengo.
  const totalDays = diffDays(dueDate, end || calculationDate || new Date());

  // Indexación UI (Decreto-Ley 14.500).
  const uS = Number(uiStart) || 0;
  const uE = Number(uiEnd) || 0;
  const factorUI = uS > 0 && uE > 0 ? uE / uS : 1;
  const capitalReajustado = rnd(debt * factorUI);
  const reajuste = rnd(capitalReajustado - debt);

  const dailyRate = rate / 100 / 365;

  if (debt <= 0 || days <= 0) {
    return {
      interest: 0, reajuste: 0, moraTotal: 0, capitalReajustado: debt,
      daysOverdue: totalDays, effectiveDays: days, rate, dailyRate: 0,
      method: interestMethod, factorUI: 1, uiStart: uS || null, uiEnd: uE || null,
    };
  }

  let interest = 0;
  if (interestMethod === "monto_fijo") {
    // Monto fijo mensual por mora, topeado al equivalente del 12% anual del
    // capital (reajustado) por los días efectivos.
    const fixedAmount = Number(interestFixedAmount) || 0;
    const maxLegalInterest = rnd(capitalReajustado * (12 / 100 / 365) * days);
    interest = Math.min(fixedAmount, maxLegalInterest);
  } else if (rate > 0) {
    // Porcentaje (default): I = capitalReajustado * r * (días/365). No capitaliza.
    interest = rnd(capitalReajustado * dailyRate * days);
  }

  return {
    interest,
    reajuste,
    moraTotal: rnd(reajuste + interest),
    capitalReajustado,
    daysOverdue: totalDays,
    effectiveDays: days,
    rate,
    dailyRate,
    method: interestMethod,
    factorUI,
    uiStart: uS || null,
    uiEnd: uE || null,
  };
}

/**
 * Calcula intereses para una lista de pagos vencidos.
 *
 * @param {Array} overduePayments - Pagos vencidos [{id, amount, remainingAmount, maturity, unitId, userid, month, year}]
 * @param {number} annualRate - Tasa anual del edificio
 * @param {Date} calculationDate - Fecha de cálculo
 * @returns {Array} [{paymentId, unitId, userid, originalDebt, dueDate, daysOverdue, interest, rate, month, year, include}]
 *   interest, reajuste y moraTotal en pesos enteros.
 */
export function calculateInterestsForOverduePayments(overduePayments, annualRate, calculationDate, { gracePeriodDays = 0, interestMethod = "porcentaje", interestFixedAmount = 0 } = {}) {
  const calcDate = calculationDate || new Date();

  return overduePayments
    .map((p) => {
      const debt = Number(p.remainingAmount) || Number(p.amount) || 0;
      const result = calculateMoraInterest({
        originalDebt: debt,
        dueDate: p.maturity || p.dueDate,
        calculationDate: calcDate,
        annualRate,
        gracePeriodDays,
        interestMethod,
        interestFixedAmount,
        // Modo incremental + indexación: el llamador (CierreMes) puede pasar el
        // período de devengo ya acotado y los valores de UI por ítem.
        accrualStartDate: p.accrualStartDate || null,
        accrualEndDate: p.accrualEndDate || null,
        uiStart: p.uiStart || null,
        uiEnd: p.uiEnd || null,
      });
      // Lo que se cobra en el cierre va en pesos enteros: recargo y reajuste se
      // redondean por separado y el total es su suma (sin centésimos).
      const interest = roundPesos(result.interest);
      const reajuste = roundPesos(result.reajuste);
      const moraTotal = interest + reajuste;

      return {
        paymentId: p.id,
        unitId: p.unitId || p.apartmentid,
        apartmentId: p.apartmentId || null,
        userid: p.userid,
        originalDebt: debt,
        dueDate: p.maturity || p.dueDate,
        daysOverdue: result.daysOverdue,
        effectiveDays: result.effectiveDays,
        interest,
        reajuste,
        moraTotal,
        capitalReajustado: result.capitalReajustado,
        factorUI: result.factorUI,
        uiStart: result.uiStart,
        uiEnd: result.uiEnd,
        accrualStartDate: p.accrualStartDate || null,
        accrualEndDate: p.accrualEndDate || null,
        rate: result.rate,
        method: result.method,
        month: p.month,
        year: p.year,
        include: moraTotal > 0,
      };
    })
    .filter((item) => item.moraTotal > 0 || item.daysOverdue > 0);
}
